<template>
  <div class="max-w-4xl mx-auto">
    <!-- 標題卡片 -->
    <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6 post-shadow">
      <div class="flex items-center space-x-3">
        <div class="w-12 h-12 bg-gradient-to-br from-wood-500 to-wood-600 rounded-xl flex items-center justify-center">
          <i class="fas fa-plus-circle text-white text-2xl"></i>
        </div>
        <div>
          <h3 class="text-2xl font-bold text-wood-800">新增拳譜</h3>
          <p class="text-sm text-wood-600 mt-1">建立新的太極拳拳譜資料</p>
        </div>
      </div>
    </div>

    <!-- 表單卡片 -->
    <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
      <!-- 錯誤訊息 -->
      <div v-if="errors.length > 0" class="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
        <div class="flex items-start">
          <i class="fas fa-exclamation-circle text-red-500 mt-1 mr-3"></i>
          <div class="flex-1">
            <h4 class="font-semibold text-red-800 mb-2">發生 {{ errors.length }} 個錯誤:</h4>
            <ul class="list-disc list-inside text-red-600 space-y-1">
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 表單 -->
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- 拳譜名稱 -->
        <div>
          <label for="name" class="block text-sm font-medium text-wood-800 mb-2">
            拳譜名稱 <span class="text-red-500">*</span>
          </label>
          <input
            id="name"
            v-model="formData.name"
            type="text"
            class="w-full px-4 py-3 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none focus:ring-2 focus:ring-wood-500/20 bg-white text-wood-800 placeholder-wood-400"
            placeholder="例如: 楊家老架太極拳37式"
            required
          />
          <p class="mt-2 text-sm text-wood-600 flex items-center">
            <i class="fas fa-info-circle mr-2"></i>
            此名稱必須與 public/ 目錄下的 JSON 檔案名稱相同(不含 .json 副檔名)
          </p>
        </div>

        <!-- 拳譜描述 -->
        <div>
          <label for="description" class="block text-sm font-medium text-wood-800 mb-2">
            拳譜描述 <span class="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            v-model="formData.description"
            rows="5"
            class="w-full px-4 py-3 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none focus:ring-2 focus:ring-wood-500/20 bg-white text-wood-800 placeholder-wood-400"
            placeholder="請輸入拳譜的簡短描述..."
            required
          ></textarea>
          <p class="mt-2 text-sm text-wood-600 flex items-center">
            <i class="fas fa-info-circle mr-2"></i>
            簡要說明此拳譜的特色、來源或適用對象
          </p>
        </div>

        <!-- 按鈕組 -->
        <div class="flex gap-3 pt-4">
          <button
            type="submit"
            :disabled="isSubmitting"
            class="text-wood border-1 flex-1 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 disabled:from-wood-300 disabled:to-wood-400 px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <i :class="isSubmitting ? 'fas fa-spinner animate-spin' : 'fas fa-save'"></i>
            <span>{{ isSubmitting ? '建立中...' : '建立拳譜' }}</span>
          </button>
          <button
            type="button"
            @click="handleCancel"
            class="px-6 py-3 bg-wood-200 hover:bg-wood-300 text-wood-800 rounded-xl transition-all duration-300 flex items-center space-x-2"
          >
            <i class="fas fa-times"></i>
            <span>取消</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { usePostsStore } from '../stores/posts.js'
import axios from 'axios'

const postsStore = usePostsStore()

// 表單資料
const formData = ref({
  name: '',
  description: ''
})

// 狀態管理
const isSubmitting = ref(false)
const errors = ref([])

// 取得 CSRF token
const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

// 處理表單提交
const handleSubmit = async () => {
  errors.value = []

  // 前端驗證
  if (!formData.value.name.trim()) {
    errors.value.push('請輸入拳譜名稱')
  }
  if (!formData.value.description.trim()) {
    errors.value.push('請輸入拳譜描述')
  }

  if (errors.value.length > 0) {
    return
  }

  isSubmitting.value = true

  try {
    const response = await axios.post('/boxing_charts', {
      boxing_chart: {
        name: formData.value.name,
        description: formData.value.description
      }
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Accept': 'text/html'
      }
    })

    // 成功後重導向到拳譜詳情頁
    const boxingChartId = response.data?.id || extractIdFromResponse(response)

    if (boxingChartId) {
      window.location.href = `/boxing_charts/${boxingChartId}`
    } else {
      // 如果無法取得 ID，導向列表頁
      window.location.href = '/boxing_charts'
    }
  } catch (error) {
    console.error('建立拳譜失敗:', error)

    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.error) {
      errors.value = [error.response.data.error]
    } else {
      errors.value = ['建立拳譜時發生錯誤，請稍後再試']
    }

    isSubmitting.value = false
  }
}

// 從回應中提取 ID（用於處理 HTML 回應的情況）
const extractIdFromResponse = (response) => {
  // 嘗試從 response 的 request.responseURL 提取 ID
  const url = response.request?.responseURL || ''
  const match = url.match(/\/boxing_charts\/(\d+)/)
  return match ? match[1] : null
}

// 處理取消
const handleCancel = () => {
  // 導航到拳譜列表頁
  window.history.pushState(null, '', '/boxing_charts')
  postsStore.updateCurrentPath('/boxing_charts')
}
</script>

<style scoped>
/* 使用全域定義的木質風格樣式 */
</style>
